import { useState, useMemo } from 'react'
import { useActionItems } from '../queries/useActionItems.js'
import { timeAgo } from '../data/metaKeys.js'
import { colorFor } from '../data/userColors.js'

const TEAM_OWNERS = [
  '—',
  'Ricky',
  'Zoe',
  'Christina',
  'Katie',
  'Margo',
  'Emily',
  'Sandra',
  'Sergina',
  'Jonathan',
  'Chris',
  'Harold',
  'Jessie',
  'Christine',
  'Braden',
  'Colin',
  'Jane',
  'TBD',
]

/**
 * ActionItems — per-goal action item section.
 *
 * Mirrors q2-goals.html's ActionItemsSection:
 *   ⚡ Action Items  [N open]  [M overdue]   ▼/▶
 *   [ ] Task name                   [Owner chip]  📅 Apr 21  · + Add update
 *       └── (update note)
 *   [ ] Another task…
 *
 *   + Add an item (click)  → "What needs to happen?" + owner + date + Add
 */
export default function ActionItems({
  pillarName,
  goalIndex,
  goalLabel,
  defaultOwner, // typically the goal's DRI
}) {
  const { items, create, toggle, remove, update } = useActionItems(
    pillarName,
    goalIndex
  )

  const open = useMemo(() => items.filter((a) => a.status === 'open'), [items])
  const done = useMemo(() => items.filter((a) => a.status === 'done'), [items])
  const overdue = useMemo(
    () => open.filter((a) => isDue(a.dueDate)),
    [open]
  )
  const ordered = [...open, ...done]

  // Section open/closed (default: open if there are any items)
  const [isOpen, setIsOpen] = useState(items.length > 0)

  return (
    <div
      data-tour="action-items"
      style={{
        marginTop: 12,
        padding: '10px 12px',
        background: '#fafafa',
        border: '1px solid var(--border)',
        borderRadius: 10,
      }}
    >
      <SectionHeader
        openCount={open.length}
        overdueCount={overdue.length}
        isOpen={isOpen}
        onToggle={() => setIsOpen((x) => !x)}
      />

      {isOpen && (
        <div style={{ marginTop: 4 }}>
          {ordered.map((item) => (
            <ActionItemRow
              key={item.id}
              item={item}
              onToggle={() => toggle(item.id)}
              onRemove={() => remove(item.id)}
              onUpdate={(text) => update(item.id, text)}
            />
          ))}
          {ordered.length === 0 && (
            <div
              style={{
                fontSize: 11,
                color: 'var(--muted-light)',
                fontStyle: 'italic',
                padding: '6px 0 10px',
              }}
            >
              No action items yet.
            </div>
          )}

          <AddActionItemForm
            onAdd={(task, owner, dueDate) => create({ task, owner, dueDate })}
            defaultOwner={defaultOwner || '—'}
          />
        </div>
      )}
    </div>
  )
}

/* ─── Section header ─────────────────────────────────────────────────── */

function SectionHeader({ openCount, overdueCount, isOpen, onToggle }) {
  return (
    <div
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <span style={{ fontSize: 13 }}>⚡</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>
        Action Items
      </span>
      {openCount > 0 && (
        <span
          style={{
            background: '#fef3c7',
            color: '#b45309',
            border: '1px solid #fde68a',
            borderRadius: 10,
            padding: '1px 7px',
            fontSize: 10,
            fontWeight: 800,
          }}
        >
          {openCount} open
        </span>
      )}
      {overdueCount > 0 && (
        <span
          style={{
            background: '#fee2e2',
            color: '#dc2626',
            border: '1px solid #fca5a5',
            borderRadius: 10,
            padding: '1px 7px',
            fontSize: 10,
            fontWeight: 800,
          }}
        >
          {overdueCount} overdue
        </span>
      )}
      <span style={{ marginLeft: 'auto', color: 'var(--muted-light)', fontSize: 12 }}>
        {isOpen ? '▼' : '▶'}
      </span>
    </div>
  )
}

/* ─── Single row ─────────────────────────────────────────────────────── */

function ActionItemRow({ item, onToggle, onRemove, onUpdate }) {
  const [editingUpdate, setEditingUpdate] = useState(false)
  const [updateDraft, setUpdateDraft] = useState(item.lastUpdate || '')
  const [hovered, setHovered] = useState(false)

  const isOverdue = item.status === 'open' && isDue(item.dueDate)
  const isSoon = item.status === 'open' && dueSoon(item.dueDate)
  const isDone = item.status === 'done'
  const ownerColor = colorFor(item.owner)

  const handleSaveUpdate = () => {
    if (updateDraft.trim() !== (item.lastUpdate || '').trim()) {
      onUpdate(updateDraft.trim())
    }
    setEditingUpdate(false)
  }

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '8px 0',
        borderBottom: '1px solid #f3f4f6',
        opacity: isDone ? 0.55 : 1,
      }}
    >
      {/* Checkbox */}
      <div
        onClick={onToggle}
        style={{
          width: 18,
          height: 18,
          borderRadius: 4,
          flexShrink: 0,
          marginTop: 2,
          border: `2px solid ${isDone ? '#16a34a' : '#d1d5db'}`,
          background: isDone ? '#16a34a' : '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.15s',
        }}
      >
        {isDone && (
          <span style={{ color: '#fff', fontSize: 10, fontWeight: 800, lineHeight: 1 }}>
            ✓
          </span>
        )}
      </div>

      {/* Task + metadata */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--text-secondary)',
            textDecoration: isDone ? 'line-through' : 'none',
            lineHeight: 1.4,
          }}
        >
          {item.task}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 3,
            flexWrap: 'wrap',
          }}
        >
          {item.owner && item.owner !== '—' && (
            <span
              style={{
                background: ownerColor + '18',
                color: ownerColor,
                border: `1px solid ${ownerColor}30`,
                borderRadius: 20,
                padding: '1px 7px',
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              {item.owner}
            </span>
          )}
          {item.dueDate && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: isOverdue ? '#dc2626' : isSoon ? '#d97706' : 'var(--muted-light)',
              }}
            >
              {isOverdue ? '⚠ ' : isSoon ? '🕐 ' : '📅 '}
              {fmtDue(item.dueDate)}
              {isOverdue && ' · Overdue'}
            </span>
          )}
          {item.createdBy && item.createdBy !== item.owner && (
            <span style={{ fontSize: 10, color: '#c4c9d4' }}>
              added by {item.createdBy}
            </span>
          )}

          {!isDone && !editingUpdate && (
            <span
              onClick={() => {
                setEditingUpdate(true)
                setUpdateDraft(item.lastUpdate || '')
              }}
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: 'var(--muted)',
                cursor: 'pointer',
                textDecoration: 'underline',
                textDecorationStyle: 'dotted',
              }}
            >
              {item.lastUpdate ? 'Edit update' : '+ Add update'}
            </span>
          )}

          <span
            onClick={() => {
              if (confirm(`Delete action item "${item.task}"?`)) onRemove()
            }}
            title="Delete action item"
            style={{
              marginLeft: 'auto',
              color: 'var(--muted-light)',
              fontSize: 11,
              cursor: 'pointer',
              opacity: hovered ? 1 : 0,
              transition: 'opacity 0.15s',
            }}
          >
            ✕
          </span>
        </div>

        {/* Existing update note */}
        {item.lastUpdate && !editingUpdate && (
          <div
            style={{
              marginTop: 6,
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: 6,
              padding: '5px 9px',
              fontSize: 11,
              color: '#166534',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 6,
              lineHeight: 1.5,
            }}
          >
            <span style={{ fontSize: 11 }}>💬</span>
            <div style={{ flex: 1 }}>
              <span style={{ fontWeight: 700 }}>
                {item.lastUpdateBy || 'Unknown'}:
              </span>{' '}
              {item.lastUpdate}
              {item.lastUpdateAt && (
                <span
                  style={{ color: '#6ee7b7', marginLeft: 6, fontWeight: 500 }}
                >
                  · {timeAgo(new Date(item.lastUpdateAt).getTime())}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Update editor */}
        {editingUpdate && (
          <div style={{ marginTop: 6 }}>
            <textarea
              autoFocus
              value={updateDraft}
              onChange={(e) => setUpdateDraft(e.target.value)}
              onBlur={handleSaveUpdate}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setEditingUpdate(false)
                  setUpdateDraft(item.lastUpdate || '')
                }
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  handleSaveUpdate()
                }
              }}
              placeholder="Quick update on this action item…"
              style={{
                width: '100%',
                minHeight: 40,
                fontSize: 11,
                fontFamily: 'inherit',
                color: 'var(--text-secondary)',
                border: '1px solid var(--accent)',
                borderRadius: 6,
                padding: '6px 8px',
                background: '#fff',
                resize: 'vertical',
                lineHeight: 1.5,
                outline: 'none',
              }}
            />
            <div style={{ fontSize: 9, color: 'var(--muted-light)', marginTop: 2 }}>
              click away to save · Esc to cancel
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/* ─── Add form ───────────────────────────────────────────────────────── */

function AddActionItemForm({ onAdd, defaultOwner }) {
  const [open, setOpen] = useState(false)
  const [task, setTask] = useState('')
  const [owner, setOwner] = useState(defaultOwner || '—')
  const [dueDate, setDueDate] = useState('')

  const reset = () => {
    setTask('')
    setOwner(defaultOwner || '—')
    setDueDate('')
    setOpen(false)
  }

  const submit = () => {
    if (!task.trim()) return
    onAdd(task.trim(), owner, dueDate)
    reset()
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          marginTop: 8,
          background: 'transparent',
          border: '1px dashed var(--border-strong)',
          borderRadius: 8,
          padding: '6px 12px',
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--muted)',
          cursor: 'pointer',
          width: '100%',
          textAlign: 'left',
        }}
      >
        + Add action item
      </button>
    )
  }

  return (
    <div
      style={{
        marginTop: 10,
        padding: 10,
        background: '#fff',
        border: '1px solid var(--border)',
        borderRadius: 8,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <input
        autoFocus
        type="text"
        value={task}
        onChange={(e) => setTask(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit()
          if (e.key === 'Escape') reset()
        }}
        placeholder="What needs to happen?"
        style={{
          padding: '8px 10px',
          border: '1px solid var(--accent)',
          borderRadius: 6,
          fontSize: 12,
          fontFamily: 'inherit',
          outline: 'none',
        }}
      />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <select
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          style={{
            flex: '1 1 120px',
            padding: '7px 10px',
            border: '1px solid var(--border)',
            borderRadius: 6,
            fontSize: 12,
            background: '#fff',
            cursor: 'pointer',
          }}
        >
          {TEAM_OWNERS.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          style={{
            flex: '1 1 120px',
            padding: '7px 10px',
            border: '1px solid var(--border)',
            borderRadius: 6,
            fontSize: 12,
            background: '#fff',
          }}
        />
        <button
          type="button"
          onClick={submit}
          disabled={!task.trim()}
          style={{
            padding: '7px 14px',
            border: 'none',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 700,
            background: task.trim() ? '#2563eb' : '#e5e7eb',
            color: task.trim() ? '#fff' : '#9ca3af',
            cursor: task.trim() ? 'pointer' : 'not-allowed',
          }}
        >
          Add
        </button>
        <button
          type="button"
          onClick={reset}
          style={{
            padding: '7px 14px',
            border: '1px solid var(--border)',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            background: '#fff',
            color: 'var(--muted)',
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

function isDue(dateStr) {
  if (!dateStr) return false
  const d = new Date(dateStr + 'T23:59:59')
  return d < new Date()
}

function dueSoon(dateStr) {
  if (!dateStr) return false
  const d = new Date(dateStr + 'T23:59:59')
  const diff = (d - new Date()) / 86400000
  return diff >= 0 && diff <= 2
}

function fmtDue(dateStr) {
  if (!dateStr) return null
  return new Date(dateStr + 'T12:00:00').toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
  })
}

